import { AlertTriangle, Globe } from "lucide-react"
import type { PreviewState } from "@/components/automations/use-audience-preview"

/**
 * What "all of IUPAT" actually means, in numbers, before anyone saves it.
 *
 * IUPAT is the parent of every District Council and every Local, so covering it
 * reaches the entire union — which is a very different thing from a District
 * Council covering its twenty Locals. Two hard limits sit in front of that, and
 * a run that trips either is refused rather than partly sent, so the screen
 * says which one applies while the choice is still being made.
 */
export function UnionWideNotice({
  preview,
  sendCeiling,
  maxMatched,
  dryRun,
}: {
  preview: PreviewState
  sendCeiling: number
  maxMatched: number
  /** What is actually in force app-wide, not what the switch says. */
  dryRun: boolean
}) {
  const result = preview.result
  const matched = result?.matched_count ?? null
  const reach = result?.reach_count ?? null

  // Above this the app refuses outright — it will not hold a result set that
  // large in memory. It applies to rehearsals too.
  if (matched !== null && matched > maxMatched) {
    return (
      <Notice tone="danger">
        <strong>{matched.toLocaleString()} members match today</strong> — more
        than the {maxMatched.toLocaleString()} the app will process in one run.
        A run this wide is refused before anything is sent, rehearsal included.
        Narrow it to specific District Councils, or give the automation a
        tighter query.
      </Notice>
    )
  }

  // The ceiling does not apply to a rehearsal, but it is what will happen the
  // moment Rehearsal is switched off — so it is said either way, framed for
  // whichever is actually in force.
  if (reach !== null && reach > sendCeiling) {
    return (
      <Notice tone="danger">
        <strong>This would text {reach.toLocaleString()} members</strong> — above
        the send ceiling of {sendCeiling.toLocaleString()}.{" "}
        {dryRun
          ? "Rehearsal is on, so a run records everyone and sends nothing. With Rehearsal off it would be refused instead."
          : "A real run will be refused until an admin raises the ceiling under Admin → Delivery."}{" "}
        Either way the refusal is recorded in History with these numbers.
      </Notice>
    )
  }

  return (
    <Notice tone="signal">
      This reaches <strong>every member of the union</strong> — every District
      Council, every Local, and the Locals that belong to no District Council.
      {reach !== null && ` ${reach.toLocaleString()} people would be texted today.`}
    </Notice>
  )
}

function Notice({
  tone,
  children,
}: {
  tone: "danger" | "signal"
  children: React.ReactNode
}) {
  const Icon = tone === "danger" ? AlertTriangle : Globe

  return (
    <div className={`callout callout-${tone} py-3`}>
      <p
        className={`flex items-start gap-2 text-xs ${
          tone === "danger" ? "text-danger-display" : "text-ink-body"
        }`}
      >
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{children}</span>
      </p>
    </div>
  )
}
